import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { v4 as uuid } from 'uuid'
import {
  FlowCore,
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowTransition,
} from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { recursiveCleanObject } from '../../../modules/utils'
import { isRepresentationValid, validateRepresentation } from './representation-validation'

Locale.getInstance().registerKeySet('fr', 'lvcCreateEventContainer', {
  eventCreationFailed: "Impossible de créer l'événement. Veuillez réessayer plus tard.",
  submitBlockedInvalid:
    'Certaines représentations comportent des erreurs. Veuillez les corriger avant de soumettre.',
})

Locale.getInstance().registerKeySet('en', 'lvcCreateEventContainer', {
  eventCreationFailed: 'Failed to create event. Please try again later.',
  submitBlockedInvalid: 'Some representations contain errors. Please fix them before submitting.',
})

export interface LvcCreateEventContainer {}

const STEPS = {
  CREATE_EVENT_SEARCH_OR_ADD: 'createEventSearchOrAdd',
  CREATE_EVENT_DESCRIPTION: 'createEventDescription',
  CREATE_EVENT_IMAGE: 'createEventAddImage',
  ADD_EVENT_CONTRIBUTORS: 'addEventContributors',
  CREATE_EVENT_CONFIRMATION: 'eventConfirmation',
  CREATE_EVENT_FIRST_RECAP: 'eventFirstRecap',
  ADD_REPRESENTATION: 'addRepresentation',
  FINAL_EVENT_RECAP: 'finalEventRecap',
  EVENT_SUBMITTED: 'eventSubmitted',
}

const CORE_CONFIG: IFlowCoreConfig = {
  initialStep: STEPS.CREATE_EVENT_SEARCH_OR_ADD,
  eventType: 'event',
  calcProgress: (data: any, currentStep: IFlowTransition, transitions: IFlowTransition[]) => {
    let indexOf = transitions.indexOf(currentStep)

    return Math.round(100 * (indexOf / (transitions.length - 1)))
  },
  transitions: [
    {
      stepName: STEPS.CREATE_EVENT_SEARCH_OR_ADD,
      elementName: (contextData: any) => `

        <lvc-create-event-search-or-add flow-step="${STEPS.CREATE_EVENT_SEARCH_OR_ADD}" flow-group="${contextData.flowGroup}">
        </lvc-create-event-search-or-add>
  		`,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EVENT_DESCRIPTION
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_SEARCH_OR_ADD
      },
    },
    {
      stepName: STEPS.CREATE_EVENT_DESCRIPTION,
      elementName: (contextData: any) => `
			<lvc-create-event-description flow-step="${STEPS.CREATE_EVENT_DESCRIPTION}" flow-group="${contextData.flowGroup}">
			</lvc-create-event-description>
		`,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EVENT_IMAGE
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_SEARCH_OR_ADD
      },
    },
    {
      stepName: STEPS.CREATE_EVENT_IMAGE,
      elementName: (contextData: any) => `
			<lvc-create-event-add-image flow-step="${STEPS.CREATE_EVENT_IMAGE}" flow-group="${contextData.flowGroup}">
			</lvc-create-event-add-image>
		`,
      transitionNext: async (data: any) => {
        return STEPS.ADD_EVENT_CONTRIBUTORS
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_DESCRIPTION
      },
    },
    {
      stepName: STEPS.ADD_EVENT_CONTRIBUTORS,
      elementName: (contextData: any) => `
      <lvc-create-event-add-contributors flow-step="${STEPS.ADD_EVENT_CONTRIBUTORS}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-add-contributors>
    `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EVENT_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_IMAGE
      },
    },
    {
      stepName: STEPS.CREATE_EVENT_CONFIRMATION,
      elementName: (contextData: any) => `
      <lvc-create-event-final-recap data-confirmation-mode="true" flow-step="${STEPS.CREATE_EVENT_CONFIRMATION}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-final-recap>
    `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EVENT_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_SEARCH_OR_ADD
      },
    },
    {
      stepName: STEPS.CREATE_EVENT_FIRST_RECAP,
      elementName: (contextData: any) => `
      <lvc-create-event-representations flow-step="${STEPS.CREATE_EVENT_FIRST_RECAP}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-representations>
    `,
      transitionNext: async (data: any) => {
        if (data?.addRepresentation?.representations?.length > 0) {
          return STEPS.FINAL_EVENT_RECAP
        }
        return STEPS.ADD_REPRESENTATION
      },
      transitionPrev: async (data: any) => {
        if (data?.control_modifyingExistingEvent) {
          return STEPS.CREATE_EVENT_CONFIRMATION
        }
        return STEPS.ADD_EVENT_CONTRIBUTORS
      },
    },
    // {
    //   stepName: STEPS.CREATE_EVENT_FIRST_RECAP,
    //   elementName: (contextData: any) => `
    //   <lvc-create-event-first-recap flow-step="${STEPS.CREATE_EVENT_FIRST_RECAP}" flow-group="${contextData.flowGroup}">
    //   </lvc-create-event-first-recap>
    //   <lvc-create-representations flow-step="${STEPS.CREATE_EVENT_FIRST_RECAP}" flow-group="${contextData.flowGroup}">
    //   </lvc-create-representations>
    // `,
    //   transitionNext: async (data: any) => {
    //     if (data?.addRepresentation?.representations?.length > 0) {
    //       return STEPS.FINAL_EVENT_RECAP;
    //     }
    //     return STEPS.ADD_REPRESENTATION;
    //   },
    //   transitionPrev: async (data: any) => {
    //     return STEPS.ADD_EVENT_CONTRIBUTORS;
    //   },
    // },
    {
      stepName: STEPS.ADD_REPRESENTATION,
      elementName: (contextData: any) => `
      <lvc-create-event-add-representation flow-step="${STEPS.ADD_REPRESENTATION}" flow-group="${contextData.flowGroup}" enable-contributors="true">
      </lvc-create-event-add-representation>
    `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_EVENT_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_FIRST_RECAP
      },
    },
    {
      stepName: STEPS.FINAL_EVENT_RECAP,
      elementName: (contextData: any) => `
      <lvc-create-event-final-recap flow-step="${STEPS.FINAL_EVENT_RECAP}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-final-recap>
    `,
      transitionNext: async (data: any) => {
        return STEPS.EVENT_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_EVENT_FIRST_RECAP
      },
    },
    {
      stepName: STEPS.EVENT_SUBMITTED,
      elementName: (contextData: any) => `
      <lvc-create-event-submitted flow-step="${STEPS.EVENT_SUBMITTED}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-submitted>
    `,
      transitionNext: async (data: any) => {
        return STEPS.EVENT_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.FINAL_EVENT_RECAP
      },
    },
  ] as IFlowTransition[],

  onSave: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const draftData = data

    const userId = MainModule.getInstance().getAuth().getUserAuth()?.userId

    // if we are on the 'search or add' step, (first step) we don't want to save the data
    if (instance.getCurrentTransitionStep().stepName === STEPS.CREATE_EVENT_SEARCH_OR_ADD) {
      return
    }

    await MainModule.getInstance()
      .getAuth()
      .setUserCustomDocument(
        `event_drafts/${instance.entryId}`,
        recursiveCleanObject({
          draftData,
          lastModified: new Date().toISOString(),
        })
      )
  },

  onCommit: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const flowCore = MainModule.getInstance().getFlowManager().getCore('eventCreation')

    // Final submission gate (PRO-48): never send an event whose representations
    // still have validation errors — even if it slipped past a step (autosaved
    // draft, back-navigation, etc.). The draft is preserved so the user can fix.
    const representations = (data as any)?.addRepresentation?.representations || []
    const allRepresentationsValid =
      representations.length > 0 &&
      representations.every((representation: any) =>
        isRepresentationValid(validateRepresentation(representation, 'event'))
      )
    if (!allRepresentationsValid) {
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateEventContainer.submitBlockedInvalid'),
        5000,
        true
      )
      return
    }

    // Call the backend to create the event
    const response = await mainModule
      .getAuth()
      .createContentItem(flowCore?.entryId || uuid(), data, 'event')

    if (!response.success) {
      // If unsuccessful, show error (draft is preserved)
      console.error('Error during event creation:', response.message)
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateEventContainer.eventCreationFailed'),
        5000,
        true
      )
    } else {
      // If successful, proceed to next step
      flowCore?.next()
    }
  },
}

export class LvcCreateEventContainer extends MinzeElement implements MinzeElement, IFlowContainer {
  _authHandler: any = null
  flowGroup: string = 'eventCreation'
  attrs: Attrs = []
  reactive: Reactive = ['stepName']
  flowCore: IFlowCore | null = null
  renderElement: string = ''
  draftId: string | null = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler)
    await this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    if (!data.isLoggedIn) {
      console.warn('User is not logged in. Aborting draft fetch.')
      return
    }
    const mainModule = MainModule.getInstance()

    this.flowCore = new FlowCore({
      ...CORE_CONFIG,
      flowContainer: this,
    } as IFlowCoreConfig)
    mainModule.getFlowManager().putCore(this.flowCore)

    // Get the draftId from URL parameters.
    const urlParams = new URLSearchParams(window.location.search)
    this.draftId = urlParams.get('draftId')

    if (this.draftId) {
      try {
        const draftData = await mainModule.getAuth().fetchDraftEventById(this.draftId)
        if (draftData) {
          this.flowCore.entryId = this.draftId
          // Load the draft data before initialization.
          this.flowCore.loadDraft(draftData)
        }
      } catch (error) {
        console.error(`Error fetching draft for draftId ${this.draftId}:`, error)
      }
    } else {
      this.flowCore.entryId = uuid()
    }

    // draft data is loaded, initialize FlowCore.
    await this.flowCore.initialize()

    // If a draft was loaded, skip the first step because the first step is the search step.
    if (this.draftId) {
      this.flowCore.next()
    }

    // Notify footer navigation that the flow is ready.
    Minze.cast('lvc:flowcore-ready', this.flowCore)
  }

  async onContainerUpdate(data: any, step: IFlowTransition): Promise<void> {
    window.scrollTo(0, 0)
    let el: string = step?.elementName?.({ flowGroup: this.flowGroup }) || '<span>no element</span>'
    this.renderElement = el
    this.stepName = step.stepName
    this.rerender()
  }

  html = () => {
    let progress: number = this.flowCore?.getProgress() ?? 0
    let saveState = this.flowCore?.getSaveState() ?? 'not_saved'
    return `
		<div class="lvc-create-event-container">
		  <div class="lvc-content">
  			${this.renderElement}
			</div>
			<div class="lvc-footer-container">
				<lvc-footer-navigation
				  progress="${progress}"
				  flow-group="${this.flowGroup}"
				  step="${this.stepName}"
				  data-type="event"
				></lvc-footer-navigation>
			</div>
		</div>
		`
  }

  css = () => {
    return `
		.lvc-create-event-container {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			min-width: 100%;
			align-items: center;
			position: relative;
		}
		.lvc-content {
  		display: flex;
  		max-height: 100%;
  		flex-direction: column;
  		max-width: 600px;
      position: relative;
      padding-bottom: 200px;
		}
		.lvc-footer-container {
			display: flex;
			justify-content: center;
			flex-direction: column;
			margin-top: auto;
			width: 100%;
			position: fixed;
			bottom: 0;
		}`
  }

  eventListeners: EventListeners = []
}
